import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import escapeRegExp from 'lodash/escapeRegExp';
import React, { useEffect, useMemo, useState } from 'react';

import useCustomTracking from '../../../Util/useCustomTracking';
import ErrorMessage from './ErrorMessage';

export type SelectValue = React.ComponentProps<typeof SelectOption>['value'];
type ValueType = React.ComponentProps<typeof Select>['value'];
export type Option = React.ComponentProps<typeof SelectOption>;
interface SelectProps extends React.ComponentProps<typeof Select> {
  name: string;
  options: Option[];
  search?: boolean;
  errorText?: string;
  'data-*'?: string;
}

const Component: React.FC<SelectProps> = ({
  ...props
}: SelectProps): React.ReactElement => {
  const { search, errorText } = props;
  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {search ? <SearchSelect {...props} /> : <BasicSelect {...props} />}
    </div>
  );
};

const BasicSelect: React.FC<SelectProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  disabled,
  options,
  errorText,
  ...props
}: SelectProps): React.ReactElement => {
  const [selected, setSelected] = useState(value);
  const [SelectTrackingTag, selectClick] = useCustomTracking(
    `coaching-form-select-field-${name}`,
    `coaching-form-select-field-${name}-slot`,
    {
      'data-csa-c-type': 'select',
      'data-csa-c-selected-value': selected || '',
    }
  );
  const SelectOptions = options.map((option: Option) => (
    <SelectOption
      value={option.value}
      label={option.label || option.value}
      key={`${name}_value_${option.value}`}
      disabled={option.disabled ?? false}
    >
      {option.children}
    </SelectOption>
  ));
  const handleChange = (a: ValueType): void => {
    setSelected(a);
    if (onChange) onChange({ target: { name, value: a } });
    selectClick();
  };

  return (
    <>
      <SelectTrackingTag />
      <Select
        name={name}
        value={selected}
        onChange={handleChange}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        error={!!errorText}
        errorMessage={errorText}
        width='100%'
        size='xlarge'
        {...props}
      >
        {SelectOptions}
      </Select>
    </>
  );
};

const SearchSelect: React.FC<SelectProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  disabled,
  options,
  errorText,
  ...props
}: SelectProps): React.ReactElement => {
  const [selected, setSelected] = useState(value);
  const [searchQuery, setSearchQuery] = useState();
  const [SelectTrackingTag, selectClick] = useCustomTracking(
    `coaching-form-search-select-field-${name}`,
    `coaching-form-search-select-field-${name}-slot`,
    {
      'data-csa-c-type': 'select',
      'data-csa-c-search-selected-value': selected || '',
    }
  );
  useEffect(() => selectClick(), [selected]);
  const handleChange = (a: ValueType): void => {
    setSelected(a);
    if (onChange) onChange({ target: { name, value: a } });
  };
  const searchRegExp = useMemo(
    () => new RegExp(escapeRegExp(searchQuery), 'i'),
    [searchQuery]
  );
  const matchedOptions = options.filter(
    (option: Option) =>
      !searchQuery || searchRegExp.test(option.label.toString())
  );

  return (
    <>
      <SelectTrackingTag data-csa-c-selected-value={selected} />
      <Select
        name={name}
        value={selected}
        onChange={handleChange}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        error={!!errorText}
        errorMessage={errorText}
        width='100%'
        size='xlarge'
        {...props}
      >
        {matchedOptions.map((option: Option) => (
          <SelectOption
            label={option.label}
            value={option.value}
            disabled={option.disabled ?? false}
            key={option.value}
          >
            {option.children}
          </SelectOption>
        ))}
        {!matchedOptions.length ? (
          <Column alignmentVertical='center' spacing='300' spacingInset='600'>
            <Text alignment='center'>No results</Text>
          </Column>
        ) : null}
      </Select>
    </>
  );
};

export default Component;
